import { BLOB_URL_REGEX, URL_REGEX } from '../constants/validations';

export const waitFor = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const getFileName = (path: string | null) => {
  if (!path) return null;

  // Split the path by '/' and get the last part
  const fileWithExtension = path.split('/').pop();

  // Split the filename by '.' and remove the extension
  const fileName = fileWithExtension
    .split('.')
    .slice(0, -1)
    .join('.')
    .split('-')
    .slice(0, -1)
    .pop();

  return fileName || path;
};

export const isValidUrl = (url: string): boolean => {
  return URL_REGEX.test(url);
};

export const isValidBlobUrl = (url: string): boolean => {
  return BLOB_URL_REGEX.test(url);
};

/**
 * Checks if a value is a boolean.
 */
export function isBoolean(value: any): value is boolean {
  return typeof value === 'boolean';
}

/**
 * Scrolls the window to the top of the page.
 *
 * @example
 * // Instant scroll to top
 * scrollTop();
 *
 * @example
 * // Smooth scroll to top
 * scrollTop({ options: { smooth: true } });
 */
export function scrollTop({
  behavior = 'auto',
}: { behavior?: 'smooth' | 'auto' } = {}) {
  // Use window.scrollTo to move the browser window's scroll position.
  // top: 0 ensures scrolling to the very top.
  // left: 0 ensures no horizontal scrolling.
  // behavior: applies the determined scroll animation (smooth or instant).
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: behavior,
  });
}

/**
 * Validate when the value is true having "True" or "False" but also booleans
 *
 * @example
 * isValid("True") // true
 * isValid("False") // false
 * isValid(true) // true
 * isValid(false) // false
 */
export function isValid(value: any): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();
    return lowerValue === 'true';
  }

  return false;
}

/**
 * Encodes a configuration object into a URL as a base64 query parameter.
 *
 * @param url - The base URL to append the config to. If null, returns null.
 * @param config - The configuration object to encode. If null or empty, returns the original URL.
 * @returns The URL with the encoded config parameter, or null if url is null.
 *
 * @example
 * encodeConfigToUrl('https://example.com', { theme: 'dark' })
 * // Returns: 'https://example.com?config=eyJ0aGVtZSI6ImRhcmsifQ=='
 *
 * encodeConfigToUrl('https://example.com?foo=bar', { theme: 'dark' })
 * // Returns: 'https://example.com?foo=bar&config=eyJ0aGVtZSI6ImRhcmsifQ=='
 */
export function encodeConfigToUrl(
  url: string | null,
  config: object | null | undefined,
): string | null {
  if (url === null) {
    return null;
  }

  if (!config || Object.keys(config).length === 0) {
    return url;
  }

  const configBase64 = btoa(JSON.stringify(config));
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}config=${configBase64}`;
}

/**
 * Decodes a configuration object from a URL's config query parameter.
 * If multiple config parameters exist, uses the last one.
 *
 * @param urlOrString - The URL string containing the encoded config parameter.
 * @returns The decoded configuration object, or null if no valid config is found.
 *
 * @example
 * decodeConfigFromUrl('https://example.com?config=eyJ0aGVtZSI6ImRhcmsifQ==')
 * // Returns: { theme: 'dark' }
 *
 * decodeConfigFromUrl('https://example.com')
 * // Returns: null
 */
export function decodeConfigFromUrl(urlOrString: string): object | null {
  try {
    const configMatches = urlOrString.match(/[?&]config=([^&]+)/g);

    if (!configMatches || configMatches.length === 0) {
      return null;
    }

    const lastConfigMatch = configMatches[configMatches.length - 1];
    const configBase64 = lastConfigMatch.split('=')[1];

    if (!configBase64) {
      return null;
    }

    return JSON.parse(atob(configBase64));
  } catch (error) {
    console.error('Error decoding config from URL:', error);
    return null;
  }
}

/**
 * Extracts the base URL without query parameters.
 * Returns null if the input is null or a File instance.
 *
 * @param urlOrString - The URL string or File object to process.
 * @returns The base URL without query parameters, or null if input is null or a File.
 *
 * @example
 * getBaseUrlWithoutQuery('https://example.com?foo=bar&config=abc')
 * // Returns: 'https://example.com'
 *
 * getBaseUrlWithoutQuery('https://example.com')
 * // Returns: 'https://example.com'
 *
 * getBaseUrlWithoutQuery(new File([], 'test.txt'))
 * // Returns: null
 */
export function getBaseUrlWithoutQuery(
  urlOrString: string | File | null,
): string | null {
  if (urlOrString === null) {
    return null;
  }

  if (urlOrString instanceof File) {
    return null;
  }

  const baseUrl = urlOrString.split('?')[0];
  return baseUrl;
}

/**
 * Convert input to SHA-256 hash
 * @param input - the input can be an object
 * @returns
 */
export async function encodeSha256(input: object | string): Promise<string> {
  let base: string = typeof input === 'string' ? input : '';
  if (typeof input !== 'string') {
    base = JSON.stringify(input);
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(base);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/*
 * Renames keys in an object based on a provided key mapping.
 * @example
 * const data = { oldName: 'value', keepThis: 'same' };
 * const result = renameKeys(data, { oldName: 'newName' });
 * // Returns: { newName: 'value', keepThis: 'same' }
 *
 * @example
 * // Renaming multiple keys
 * const items = { eSIM_quick_transfer: [...], Watch_Activation: [...] };
 * const renamed = renameKeys(items, {
 *   eSIM_quick_transfer: 'Device_Transfer'
 * });
 * // Returns: { Device_Transfer: [...], Watch_Activation: [...] }
 */
export const renameKeys = <T extends Record<string, any>>(
  obj: T,
  keyMap: {
    [oldKey: string]: string;
  },
): T => {
  return Object.keys(obj).reduce<Record<string, any>>((acc, key) => {
    const newKey = keyMap[key] || key;
    acc[newKey] = obj[key];
    return acc;
  }, {}) as T;
};
